/**
 * UIValidator
 * Validates UI-related states and operations for the update banner
 * 
 * Skills:
 * - VALIDATE_BANNER_STATE: Ensures banner state is valid
 * - VALIDATE_MOBILE_DISPLAY: Ensures mobile compatibility
 * - VALIDATE_ACCESSIBILITY: Ensures a11y compliance
 */

class UIValidator {
  constructor() {
    this.VALID_BANNER_TYPES = ['info', 'warning', 'success', 'error'];
    this.MIN_TOUCH_TARGET = 44; // Minimum touch target size in pixels (WCAG)
    this.MIN_CONTRAST_RATIO = 4.5; // WCAG AA standard
  }

  /**
   * VALIDATE_BANNER_STATE
   * Validates that the banner state object is properly structured
   * @param {object} state - The banner state object
   * @returns {{ valid: boolean, errors: string[] }}
   */
  VALIDATE_BANNER_STATE(state) {
    const errors = [];

    if (!state || typeof state !== 'object') {
      return {
        valid: false,
        errors: ['State must be an object']
      };
    }

    // Validate visible property
    if (typeof state.visible !== 'boolean') {
      errors.push('State.visible must be a boolean');
    }

    // Validate message if banner is visible
    if (state.visible) {
      if (!state.message || typeof state.message !== 'string') {
        errors.push('Visible banner must have a message string');
      } else if (state.message.length < 1) {
        errors.push('Banner message cannot be empty');
      } else if (state.message.length > 200) {
        errors.push('Banner message should not exceed 200 characters');
      }
    }

    // Validate type if present
    if (state.type && !this.VALID_BANNER_TYPES.includes(state.type)) {
      errors.push(`Invalid banner type. Must be one of: ${this.VALID_BANNER_TYPES.join(', ')}`);
    }

    // Validate newVersion if present
    if (state.newVersion !== undefined && typeof state.newVersion !== 'string') {
      errors.push('newVersion must be a string');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * VALIDATE_MOBILE_DISPLAY
   * Validates mobile-specific display requirements
   * @param {object} config - Configuration to validate
   * @returns {{ valid: boolean, warnings: string[], errors: string[] }}
   */
  VALIDATE_MOBILE_DISPLAY(config = {}) {
    const errors = [];
    const warnings = [];

    // Check if we can detect mobile
    const isMobile = this._detectMobile();

    // Validate touch targets
    if (config.buttonHeight && config.buttonHeight < this.MIN_TOUCH_TARGET) {
      errors.push(`Button height (${config.buttonHeight}px) is below minimum touch target (${this.MIN_TOUCH_TARGET}px)`);
    }

    if (config.buttonWidth && config.buttonWidth < this.MIN_TOUCH_TARGET) {
      errors.push(`Button width (${config.buttonWidth}px) is below minimum touch target (${this.MIN_TOUCH_TARGET}px)`);
    }

    // Validate font size for readability
    if (config.fontSize && config.fontSize < 14) {
      warnings.push(`Font size (${config.fontSize}px) may be too small for mobile readability`);
    }

    // Validate banner positioning
    if (config.position) {
      const validPositions = ['top', 'bottom'];
      if (!validPositions.includes(config.position)) {
        warnings.push(`Position "${config.position}" may not be optimal for mobile. Consider "top" or "bottom"`);
      }
    }

    // Check viewport meta tag presence (can only suggest, not validate directly)
    if (isMobile && config.checkViewport) {
      warnings.push('Ensure viewport meta tag is set: <meta name="viewport" content="width=device-width, initial-scale=1">');
    }

    // Validate z-index for proper layering
    if (config.zIndex !== undefined) {
      if (typeof config.zIndex !== 'number') {
        errors.push('z-index must be a number');
      } else if (config.zIndex < 1000) {
        warnings.push('z-index may be too low to appear above other UI elements');
      }
    }

    return {
      valid: errors.length === 0,
      isMobile,
      warnings,
      errors
    };
  }

  /**
   * VALIDATE_ACCESSIBILITY
   * Validates accessibility requirements for the banner
   * @param {object} config - Configuration to validate
   * @returns {{ valid: boolean, requirements: object, errors: string[], warnings: string[] }}
   */
  VALIDATE_ACCESSIBILITY(config = {}) {
    const errors = [];
    const warnings = [];
    const requirements = {
      hasAriaLive: false,
      hasRole: false,
      hasAriaLabel: false,
      keyboardAccessible: false,
      focusVisible: false
    };

    // Check for ARIA live region
    if (config.ariaLive) {
      const validAriaLive = ['polite', 'assertive', 'off'];
      if (validAriaLive.includes(config.ariaLive)) {
        requirements.hasAriaLive = true;
      } else {
        errors.push(`Invalid aria-live value. Must be one of: ${validAriaLive.join(', ')}`);
      }
    } else {
      warnings.push('Consider adding aria-live="polite" for screen reader announcements');
    }

    // Check for role attribute
    if (config.role) {
      const validRoles = ['alert', 'status', 'banner'];
      if (validRoles.includes(config.role)) {
        requirements.hasRole = true;
      } else {
        warnings.push(`Role "${config.role}" may not be ideal. Consider: ${validRoles.join(', ')}`);
      }
    } else {
      warnings.push('Consider adding role="alert" or role="status" for semantic meaning');
    }

    // Check for aria-label or aria-labelledby
    if (config.ariaLabel || config.ariaLabelledBy) {
      requirements.hasAriaLabel = true;
    }

    // Check keyboard accessibility
    if (config.hasRefreshButton || config.hasDismissButton) {
      if (config.tabIndex !== undefined && config.tabIndex >= 0) {
        requirements.keyboardAccessible = true;
      } else {
        warnings.push('Interactive elements should be keyboard accessible (tabIndex >= 0)');
      }
    }

    // Check focus visibility
    if (config.focusStyles) {
      requirements.focusVisible = true;
    } else {
      warnings.push('Ensure focus states are visible for keyboard navigation');
    }

    // Validate color contrast if provided
    if (config.backgroundColor && config.textColor) {
      warnings.push(`Ensure color contrast ratio meets WCAG AA (${this.MIN_CONTRAST_RATIO}:1)`);
    }

    return {
      valid: errors.length === 0,
      requirements,
      errors,
      warnings
    };
  }

  /**
   * VALIDATE_BANNER_ELEMENT
   * Validates an actual DOM element (when available)
   * @param {HTMLElement} element - The banner DOM element
   * @returns {{ valid: boolean, issues: string[] }}
   */
  VALIDATE_BANNER_ELEMENT(element) {
    const issues = [];

    if (!element) {
      return {
        valid: false,
        issues: ['Element is null or undefined']
      };
    }

    if (!(element instanceof HTMLElement)) {
      return {
        valid: false,
        issues: ['Element must be an HTMLElement']
      };
    }

    // Check visibility
    const style = window.getComputedStyle(element);
    if (style.display === 'none') {
      issues.push('Element has display: none');
    }
    if (style.visibility === 'hidden') {
      issues.push('Element has visibility: hidden');
    }
    if (parseFloat(style.opacity) === 0) {
      issues.push('Element has opacity: 0');
    }

    // Check positioning
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      issues.push('Element has zero dimensions');
    }

    // Check if in viewport
    const inViewport = (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth
    );
    if (!inViewport) {
      issues.push('Element is not fully within viewport');
    }

    // Check z-index
    const zIndex = parseInt(style.zIndex, 10);
    if (isNaN(zIndex) || zIndex < 1000) {
      issues.push('z-index may be too low for proper visibility');
    }

    return {
      valid: issues.length === 0,
      issues,
      dimensions: {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left
      }
    };
  }

  /**
   * RUN_ALL_VALIDATIONS
   * Runs all UI validations
   * @param {object} params - Parameters for validation
   * @returns {{ allValid: boolean, results: object }}
   */
  RUN_ALL_VALIDATIONS(params = {}) {
    const results = {
      bannerState: params.state 
        ? this.VALIDATE_BANNER_STATE(params.state) 
        : { valid: true, skipped: true },
      mobileDisplay: this.VALIDATE_MOBILE_DISPLAY(params.mobileConfig || {}),
      accessibility: this.VALIDATE_ACCESSIBILITY(params.a11yConfig || {})
    };

    if (params.element) {
      results.element = this.VALIDATE_BANNER_ELEMENT(params.element);
    }

    const allValid = Object.values(results).every(r => r.valid);

    return {
      allValid,
      results
    };
  }

  /**
   * Helper: Detect if running on mobile device
   * @private
   */
  _detectMobile() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
}

export default UIValidator;
